/**
 * Share creation validation and encryption orchestration.
 *
 * Extracts business logic from useShareCreation. No React imports.
 */

import { getStoredApiKey } from '@/lib/storage/local-storage'
import {
  encryptContent,
  createPlaintextContent,
  encryptPasscodeWithApiKey,
} from '@/lib/crypto'
import { createShare } from '@/lib/api/shares'
import type { ShareFormat } from '@core/types/share'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ShareCreationInput {
  content: string
  filename: string
  passcodeOptions: {
    passcode: string
    embedInUrl: boolean
    isPublic: boolean
  }
  securityOptions: {
    expiresIn: string
    burnAfterRead: boolean
    isModifiable: boolean
  }
  formatOptions: {
    format: ShareFormat
  }
  turnstileToken: string | null
  userTier: 'free' | 'starter' | 'pro'
}

export interface ShareCreationResult {
  shareId: string
  managementToken: string
  expiresAt: number
  filename: string
  format: ShareFormat
  passcode: string | null
  isPublic: boolean
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export type ValidationResult =
  | { valid: true }
  | { valid: false; error: string; needsAuth?: boolean }

/**
 * Validate share creation prerequisites before starting encryption.
 */
export function validateShareCreation(input: {
  content: string
  passcodeOptions: { isPublic: boolean; passcode: string }
  turnstileToken: string | null
  userTier: 'free' | 'starter' | 'pro'
}): ValidationResult {
  if (!input.content.trim()) {
    return { valid: false, error: 'Please enter some Markdown content' }
  }

  const apiKey = getStoredApiKey()
  if (!apiKey) {
    return {
      valid: false,
      error: 'Create a free account to start sharing',
      needsAuth: true,
    }
  }

  if (!input.passcodeOptions.isPublic && !input.passcodeOptions.passcode) {
    return { valid: false, error: 'Please set a passcode' }
  }

  if (input.userTier === 'free' && !input.turnstileToken) {
    return { valid: false, error: 'Please complete the security check' }
  }

  return { valid: true }
}

// ---------------------------------------------------------------------------
// Async orchestrator
// ---------------------------------------------------------------------------

/**
 * Encrypt (or wrap as plaintext) content and create the share via the API.
 *
 * Returns the share result. Does NOT store tokens, dispatch events, or call
 * React state setters — the calling hook handles all side effects.
 */
export async function encryptAndCreateShare(
  input: ShareCreationInput,
): Promise<ShareCreationResult> {
  const apiKey = getStoredApiKey()
  if (!apiKey) {
    throw new Error('No API key available')
  }

  const finalFilename = input.filename.trim() || 'untitled'

  const shareData = input.passcodeOptions.isPublic
    ? createPlaintextContent(input.content)
    : await encryptContent(input.content, input.passcodeOptions.passcode, apiKey)

  const wrappedPasscode = !input.passcodeOptions.isPublic
    ? await encryptPasscodeWithApiKey(input.passcodeOptions.passcode, apiKey)
    : undefined

  const rawSize = new Blob([input.content]).size
  const encryptedSize = new Blob([shareData.blob]).size

  const result = await createShare(
    {
      encryptedBlob: shareData.blob,
      wrappedCek: shareData.wrappedCek,
      nonce: shareData.nonce,
      kdfSalt: shareData.kdfSalt,
      kdfParams: shareData.kdfParams,
      cipher: shareData.cipher,
      expiresIn: input.securityOptions.expiresIn,
      burnAfterRead: input.securityOptions.burnAfterRead,
      isModifiable: input.securityOptions.isModifiable,
      turnstileToken: input.turnstileToken ?? undefined,
      filename: finalFilename,
      wrappedCekApiKey: shareData.wrappedCekApiKey,
      kdfSaltApiKey: shareData.kdfSaltApiKey,
      wrappedPasscode,
      rawSize,
      encryptedSize,
      createdVia: 'ui',
      format: input.formatOptions.format,
    },
    { apiKey },
  )

  const embedPasscode =
    !input.passcodeOptions.isPublic && input.passcodeOptions.embedInUrl
      ? input.passcodeOptions.passcode
      : null

  return {
    shareId: result.shareId,
    managementToken: result.managementToken,
    expiresAt: result.expiresAt,
    filename: result.filename,
    format: result.format,
    passcode: embedPasscode,
    isPublic: input.passcodeOptions.isPublic,
  }
}
